import React, { useState, useEffect, useCallback } from 'react';
import { useAnimatedNavigate } from '../../../hooks/useAnimatedNavigate';
import { useKeycloak } from '@react-keycloak/web';
import {
  ArrowLeftIcon,
  DocumentChartBarIcon,
  PrinterIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import type { TrustAccount } from '../types/billing.types';
import { TRUST_TRANSACTION_TYPE_LABELS } from '../types/billing.types';

const API_BASE = '';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

type ReportType = 'account-summary' | 'client-balances' | 'outstanding-items' | 'matter-activity';

const REPORT_OPTIONS: { value: ReportType; label: string; description: string }[] = [
  { value: 'account-summary', label: 'Trust Account Summary', description: 'Period-based transaction summary for a trust account' },
  { value: 'client-balances', label: 'Client Balance Report', description: 'Point-in-time snapshot of all client balances vs. book balance' },
  { value: 'outstanding-items', label: 'Outstanding Items', description: 'Uncleared checks and deposits with days outstanding' },
  { value: 'matter-activity', label: 'Trust Activity by Matter', description: 'Per-matter trust balances and transaction counts' },
];

const TrustReportsPage: React.FC = () => {
  const navigate = useAnimatedNavigate();
  const { keycloak } = useKeycloak();

  const [accounts, setAccounts] = useState<TrustAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [reportType, setReportType] = useState<ReportType>('client-balances');
  const [periodStart, setPeriodStart] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [periodEnd, setPeriodEnd] = useState(new Date().toISOString().split('T')[0]);

  const [reportData, setReportData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${keycloak?.token || ''}`,
  }), [keycloak?.token]);

  // Fetch trust accounts for picker
  useEffect(() => {
    if (!keycloak?.token) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/v1/billing/trust-accounts`, { headers: getHeaders() });
        if (res.ok) {
          const json = await res.json();
          const accts = json.data || [];
          setAccounts(accts);
          if (accts.length > 0) setSelectedAccount(accts[0].trust_account_id);
        }
      } catch { /* ignore */ }
    })();
  }, [keycloak?.token, getHeaders]);

  const handleRunReport = async () => {
    if (!selectedAccount && reportType !== 'matter-activity') {
      setError('Select a trust account.');
      return;
    }
    setLoading(true);
    setError(null);
    setReportData(null);
    try {
      let url: string;
      const params = new URLSearchParams();

      switch (reportType) {
        case 'account-summary':
          params.set('period_start', periodStart);
          params.set('period_end', periodEnd);
          url = `${API_BASE}/api/v1/billing/trust-reports/account-summary/${selectedAccount}?${params}`;
          break;
        case 'client-balances':
          url = `${API_BASE}/api/v1/billing/trust-reports/client-balances/${selectedAccount}`;
          break;
        case 'outstanding-items':
          url = `${API_BASE}/api/v1/billing/trust-reports/outstanding-items/${selectedAccount}`;
          break;
        case 'matter-activity':
          if (selectedAccount) params.set('trust_account_id', selectedAccount);
          url = `${API_BASE}/api/v1/billing/trust-reports/matter-activity?${params}`;
          break;
      }

      const res = await fetch(url, { headers: getHeaders() });
      const json = await res.json().catch(() => null);
      if (!res.ok) throw new Error(json?.description || json?.error || 'Failed to generate report');
      setReportData(json.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const needsPeriod = reportType === 'account-summary';

  return (
    <div className="max-w-7xl mx-auto px-6 py-6 space-y-6 lex-tab-panel">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/billing/trust-accounts')} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <DocumentChartBarIcon className="h-6 w-6 text-teal-600 dark:text-teal-400" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Trust Reports</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Generate compliance and management reports</p>
        </div>
      </div>

      {/* Report Selector */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Report Type</label>
            <select value={reportType} onChange={(e) => { setReportType(e.target.value as ReportType); setReportData(null); }}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
              {REPORT_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {REPORT_OPTIONS.find(r => r.value === reportType)?.description}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Trust Account</label>
            <select value={selectedAccount} onChange={(e) => setSelectedAccount(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
              {accounts.map((a) => (
                <option key={a.trust_account_id} value={a.trust_account_id}>{a.account_name}</option>
              ))}
              {accounts.length === 0 && <option value="">No accounts</option>}
            </select>
          </div>
        </div>

        {needsPeriod && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Period Start</label>
              <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Period End</label>
              <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
            </div>
          </div>
        )}

        {error && <p className="text-sm text-red-500">{error}</p>}

        <div className="flex justify-end gap-2">
          {reportData && (
            <button onClick={() => window.print()}
              className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
              <PrinterIcon className="h-4 w-4" /> Print / PDF
            </button>
          )}
          <button onClick={handleRunReport} disabled={loading}
            className="px-4 py-2 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50">
            {loading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {/* Report Output */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin h-6 w-6 border-2 border-teal-500 border-t-transparent rounded-full" />
          <span className="ml-2 text-sm text-gray-500">Generating report...</span>
        </div>
      )}

      {reportData && reportType === 'account-summary' && <AccountSummaryReport data={reportData} />}
      {reportData && reportType === 'client-balances' && <ClientBalanceReportView data={reportData} />}
      {reportData && reportType === 'outstanding-items' && <OutstandingItemsReport data={reportData} />}
      {reportData && reportType === 'matter-activity' && <MatterActivityReport data={reportData} />}
    </div>
  );
};

// ── Report Components ──

const AccountSummaryReport: React.FC<{ data: any }> = ({ data }) => (
  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-4 print:border-none print:p-0">
    <h2 className="text-lg font-bold text-gray-900 dark:text-white">Trust Account Summary</h2>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
      <div><span className="text-gray-500">Account:</span><div className="font-medium">{data.account_name}</div></div>
      <div><span className="text-gray-500">Type:</span><div className="font-medium">{data.account_type}</div></div>
      <div><span className="text-gray-500">Bank:</span><div className="font-medium">{data.bank_name}</div></div>
      <div><span className="text-gray-500">Reconciliation:</span><div className="font-medium">{data.reconciliation_status}</div></div>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
      <div><span className="text-gray-500">Book Balance:</span><div className="font-bold text-lg">{formatCurrency(data.book_balance)}</div></div>
      <div><span className="text-gray-500">Client Balances:</span><div className="font-bold text-lg">{formatCurrency(data.total_client_balances)}</div></div>
      <div><span className="text-gray-500">Period Deposits:</span><div className="font-semibold text-green-600">+{formatCurrency(data.period_deposits)}</div></div>
      <div><span className="text-gray-500">Period Disbursements:</span><div className="font-semibold text-red-600">-{formatCurrency(data.period_disbursements)}</div></div>
    </div>
    <div className="text-sm text-gray-500">{data.period_transactions} transactions in period | {data.ledger_count} client ledgers</div>
  </div>
);

const ClientBalanceReportView: React.FC<{ data: any }> = ({ data }) => (
  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-4 print:border-none print:p-0">
    <div className="flex items-center justify-between">
      <h2 className="text-lg font-bold text-gray-900 dark:text-white">Client Balance Report</h2>
      <div className={`flex items-center gap-1 text-sm font-medium ${data.balanced ? 'text-green-600' : 'text-red-600'}`}>
        {data.balanced ? 'Balanced' : `Discrepancy: ${formatCurrency(data.discrepancy)}`}
      </div>
    </div>
    <div className="grid grid-cols-3 gap-4 text-sm bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
      <div><span className="text-gray-500">Book Balance:</span><div className="font-bold text-lg">{formatCurrency(data.book_balance)}</div></div>
      <div><span className="text-gray-500">Total Client Balances:</span><div className="font-bold text-lg">{formatCurrency(data.total_balance)}</div></div>
      <div><span className="text-gray-500">Report Date:</span><div className="font-medium">{formatDate(data.report_date)}</div></div>
    </div>

    {data.clients && data.clients.length > 0 && (
      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Client ID</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Matter ID</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-300">Balance</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-300">Deposits</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-300">Disbursements</th>
              <th className="px-3 py-2 text-center font-medium text-gray-600 dark:text-gray-300">Status</th>
              <th className="px-3 py-2 text-center font-medium text-gray-600 dark:text-gray-300">Alerts</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {data.clients.map((c: any) => (
              <tr key={c.ledger_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td className="px-3 py-2 font-mono text-xs text-gray-900 dark:text-white">{c.client_id.slice(0, 8)}...</td>
                <td className="px-3 py-2 font-mono text-xs text-gray-700 dark:text-gray-300">{c.matter_id ? `${c.matter_id.slice(0, 8)}...` : '-'}</td>
                <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-white">{formatCurrency(c.balance)}</td>
                <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{formatCurrency(c.total_deposits)}</td>
                <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{formatCurrency(c.total_disbursements)}</td>
                <td className="px-3 py-2 text-center">
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    c.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  }`}>{c.status}</span>
                </td>
                <td className="px-3 py-2 text-center">
                  {c.below_threshold && (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                      <ExclamationTriangleIcon className="h-3 w-3" /> Below threshold
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const OutstandingItemsReport: React.FC<{ data: any[] }> = ({ data }) => (
  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-4 print:border-none print:p-0">
    <h2 className="text-lg font-bold text-gray-900 dark:text-white">
      Outstanding Items
      <span className="text-sm font-normal text-gray-400 ml-2">({data.length})</span>
    </h2>

    {data.length === 0 ? (
      <div className="text-center py-8 text-gray-500 text-sm">No outstanding items. All transactions have cleared.</div>
    ) : (
      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Date</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Type</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Description</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Check #</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-300">Amount</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-300">Days Out</th>
              <th className="px-3 py-2 text-center font-medium text-gray-600 dark:text-gray-300">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {data.map((item: any) => (
              <tr key={item.transaction_id} className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 ${item.is_stale ? 'bg-red-50/50 dark:bg-red-900/5' : ''}`}>
                <td className="px-3 py-2 text-gray-900 dark:text-white whitespace-nowrap">{formatDate(item.date)}</td>
                <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap text-xs">
                  {TRUST_TRANSACTION_TYPE_LABELS[item.type as keyof typeof TRUST_TRANSACTION_TYPE_LABELS] || item.type}
                </td>
                <td className="px-3 py-2 text-gray-900 dark:text-white max-w-xs truncate">{item.description}</td>
                <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{item.check_number || '-'}</td>
                <td className={`px-3 py-2 text-right font-medium whitespace-nowrap ${
                  item.direction === 'credit' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                }`}>
                  {item.direction === 'credit' ? '+' : '-'}{formatCurrency(item.amount)}
                </td>
                <td className={`px-3 py-2 text-right font-medium ${item.is_stale ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                  {item.days_outstanding}d
                </td>
                <td className="px-3 py-2 text-center">
                  {item.is_stale && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300">
                      <ExclamationTriangleIcon className="h-3 w-3" /> Stale
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const MatterActivityReport: React.FC<{ data: any[] }> = ({ data }) => (
  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-4 print:border-none print:p-0">
    <h2 className="text-lg font-bold text-gray-900 dark:text-white">
      Trust Activity by Matter
      <span className="text-sm font-normal text-gray-400 ml-2">({data.length} matters)</span>
    </h2>

    {data.length === 0 ? (
      <div className="text-center py-8 text-gray-500 text-sm">No matter-level trust activity found.</div>
    ) : (
      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Matter ID</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Client ID</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-300">Balance</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-300">Deposits</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-300">Disbursements</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-300">Txns</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Last Activity</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {data.map((row: any) => (
              <tr key={row.ledger_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                <td className="px-3 py-2 font-mono text-xs text-gray-900 dark:text-white">{row.matter_id.slice(0, 8)}...</td>
                <td className="px-3 py-2 font-mono text-xs text-gray-700 dark:text-gray-300">{row.client_id.slice(0, 8)}...</td>
                <td className={`px-3 py-2 text-right font-medium ${
                  row.current_balance > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-white'
                }`}>{formatCurrency(row.current_balance)}</td>
                <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{formatCurrency(row.total_deposits)}</td>
                <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{formatCurrency(row.total_disbursements)}</td>
                <td className="px-3 py-2 text-right text-gray-900 dark:text-white">{row.transaction_count}</td>
                <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">{row.last_activity_date ? formatDate(row.last_activity_date) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {data.length > 0 && (
      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-sm">
        <span className="text-gray-500">Total across all matters: </span>
        <span className="font-bold text-gray-900 dark:text-white">{formatCurrency(data.reduce((s: number, r: any) => s + r.current_balance, 0))}</span>
      </div>
    )}
  </div>
);

export default TrustReportsPage;
